/**
 * Intégration PWA : enregistrement du service worker et invitation à installer.
 *
 * ## Pourquoi l'enregistrement est conditionné à la production
 *
 * En développement, Vite sert les modules à la demande et le service worker
 * intercepterait ces requêtes : le rechargement à chaud cesse de fonctionner, et l'on
 * débogue une version en cache sans s'en rendre compte. C'est la cause classique du
 * « mes modifications ne s'appliquent pas ».
 *
 * Le service worker n'est donc enregistré qu'en production, là où les fichiers sont
 * empreintés et où la mise en cache a un sens.
 */

import { create } from 'zustand';

/** Événement non standard, absent de `lib.dom` : on décrit la partie utilisée. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallStore {
  /** Événement conservé pour pouvoir déclencher l'invitation depuis un bouton. */
  deferredPrompt: BeforeInstallPromptEvent | null;
  /** L'application tourne-t-elle déjà en mode installé ? */
  installed: boolean;
  setDeferredPrompt: (event: BeforeInstallPromptEvent | null) => void;
  setInstalled: (installed: boolean) => void;
}

export const useInstallStore = create<InstallStore>((set) => ({
  deferredPrompt: null,
  installed: false,
  setDeferredPrompt: (deferredPrompt) => set({ deferredPrompt }),
  setInstalled: (installed) => set({ installed }),
}));

/**
 * Enregistre le service worker et capture l'invitation à installer.
 *
 * À appeler une seule fois au démarrage de l'application.
 */
export function setupPwa(): void {
  if (typeof window === 'undefined') return;

  // Le navigateur ne propose l'installation que si ces deux conditions sont réunies :
  // un manifeste valide et un service worker actif.
  window.addEventListener('beforeinstallprompt', (event) => {
    // Sans `preventDefault`, Chrome affiche sa propre bannière et l'événement est perdu :
    // on ne pourrait plus déclencher l'invitation depuis notre propre bouton, au moment
    // où l'utilisateur en a besoin plutôt qu'au hasard d'une visite.
    event.preventDefault();
    useInstallStore.getState().setDeferredPrompt(event as BeforeInstallPromptEvent);
  });

  window.addEventListener('appinstalled', () => {
    useInstallStore.getState().setInstalled(true);
    useInstallStore.getState().setDeferredPrompt(null);
  });

  // Détecte un lancement depuis l'icône installée.
  if (window.matchMedia('(display-mode: standalone)').matches) {
    useInstallStore.getState().setInstalled(true);
  }

  if (!import.meta.env.PROD) return;
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch((error: unknown) => {
      // Un échec d'enregistrement ne doit jamais empêcher l'application de fonctionner :
      // le service worker n'apporte qu'un confort de chargement.
      console.warn('Service worker non enregistré :', error);
    });
  });
}

/** Déclenche l'invitation à installer, si elle est disponible. */
export async function promptInstall(): Promise<void> {
  const { deferredPrompt, setDeferredPrompt } = useInstallStore.getState();
  if (!deferredPrompt) return;

  await deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;

  // L'invitation ne peut être déclenchée qu'une fois : on la libère dans tous les cas,
  // sinon le bouton resterait affiché sans effet.
  setDeferredPrompt(null);
  if (choice.outcome === 'accepted') useInstallStore.getState().setInstalled(true);
}
